if (!customElements.get('kyma-product-bundle')) {
  customElements.define(
    'kyma-product-bundle',
    class KymaProductBundle extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.variants = this.readVariants();
        this.optionValues = this.collectOptionValues();
        this.optionNames = this.readOptionNames();
        this.intentForm = this.querySelector('[data-kyma-bundle-intent-form]');
        this.status = this.querySelector('[data-kyma-bundle-status]');
        this.mainForm = document.getElementById(this.dataset.mainFormId);
        this.radios = [...this.querySelectorAll('.kyma-bundle__tier-radio')];
        this.tiers = [...this.querySelectorAll('[data-tier]')];
        const tierQuantities = this.tiers.map((tier) => Number(tier.dataset.tier));
        this.quantityContractValid = tierQuantities.every((quantity) => Number.isSafeInteger(quantity) && quantity > 0) && new Set(tierQuantities).size === tierQuantities.length;
        this.nativeAvailability = { key: null, status: 'UNCHECKED', available: false };
        this.availabilityVersion = 0;

        this.tiers.forEach((tier) => this.initializeTier(tier));
        this.addEventListener('change', this.handleChange);
        this.addEventListener('click', this.handleCardClick);
        this.mainForm?.addEventListener('submit', this.handleSubmit, true);

        const requestedDefault = this.dataset.defaultTier || '2';
        const defaultRadio = this.radios.find((radio) => radio.value === requestedDefault) || this.radios[0];
        if (defaultRadio) defaultRadio.checked = true;
        this.selectTier(defaultRadio?.value || '1');
        this.mappingPromise = this.loadMapping().finally(() => this.updateIntent());
      }

      disconnectedCallback() {
        this.availabilityAbortController?.abort();
        this.availabilityVersion += 1;
        this.pendingAvailabilityKey = null;
        this.removeEventListener('change', this.handleChange);
        this.removeEventListener('click', this.handleCardClick);
        this.mainForm?.removeEventListener('submit', this.handleSubmit, true);
      }

      handleChange = (event) => {
        if (event.target.matches('.kyma-bundle__tier-radio')) {
          this.selectTier(event.target.value);
          return;
        }

        if (event.target.matches('.kyma-bundle__select')) {
          const tier = event.target.closest('[data-tier]');
          this.refreshSlotAvailability(event.target.closest('[data-slot]'));
          this.updateTier(tier);
          if (tier?.classList.contains('is-selected')) this.updateIntent();
        }
      };

      handleCardClick = (event) => {
        if (event.target.closest('select, input, label, button, a')) return;
        const tier = event.target.closest('[data-tier]');
        if (!tier) return;
        const radio = this.querySelector(`.kyma-bundle__tier-radio[value="${tier.dataset.tier}"]`);
        if (!radio?.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
          radio.focus();
        }
      };

      handleSubmit = async (event) => {
        const intent = this.getIntent();
        if (this.submitReadyKey === intent.availabilityKey && intent.valid && intent.serverVerified) {
          this.submitReadyKey = null;
          if (!this.dispatchSubmitRequest(intent)) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.dataset.integration = 'qa-intercepted';
            return;
          }
          this.dataset.integration = 'native-parent';
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        if (!intent.baseValid || !intent.serverVerified) {
          this.dispatchSubmitRequest(intent);
          this.blockSubmission(intent, intent.mappingStatus || 'SERVER_INTEGRATION_UNVERIFIED');
          return;
        }

        this.dataset.integration = 'checking-availability';
        const available = await this.verifyNativeAvailability(intent, { fresh: true });
        const refreshed = this.getIntent();
        if (!available || refreshed.availabilityKey !== intent.availabilityKey || !refreshed.valid) {
          this.dispatchSubmitRequest(refreshed);
          this.blockSubmission(refreshed, refreshed.availabilityStatus || 'AVAILABILITY_UNVERIFIED');
          return;
        }

        this.submitReadyKey = refreshed.availabilityKey;
        this.mainForm.requestSubmit();
      };

      dispatchSubmitRequest(intent) {
        return this.dispatchEvent(
          new CustomEvent('kyma:bundle-submit-request', {
            bubbles: true,
            cancelable: true,
            detail: { intent, formData: new FormData(this.mainForm) },
          })
        );
      }

      blockSubmission(intent, reason) {
        this.dataset.integration = 'blocked';
        if (this.status) this.status.textContent = 'Ajout indisponible : vérifie la disponibilité du lot.';
        this.dispatchEvent(
          new CustomEvent('kyma:bundle-submit-blocked', {
            bubbles: true,
            detail: { intent, reason },
          })
        );
      }

      readVariants() {
        const node = this.querySelector('[data-kyma-bundle-variants]');
        try {
          return JSON.parse(node?.textContent || '[]').map((variant) => ({
            id: String(variant.id),
            title: variant.title,
            options: variant.options || [],
            price: Number(variant.price),
            available: Boolean(variant.available),
          }));
        } catch (error) {
          console.error('KYMA bundle: invalid product variants JSON.', error);
          return [];
        }
      }

      readOptionNames() {
        return String(this.dataset.optionLabels || '')
          .split('|')
          .map((name) => name.split(':').at(-1)?.trim())
          .filter(Boolean);
      }

      async loadMapping() {
        this.mapping = null;
        this.mappingError = null;
        if (!this.dataset.mapUrl) {
          this.mappingError = 'MAPPING_URL_MISSING';
          return;
        }
        try {
          const response = await fetch(this.dataset.mapUrl, { credentials: 'same-origin' });
          if (!response.ok) throw new Error(`HTTP_${response.status}`);
          const mapping = await response.json();
          if (mapping.schema !== 1 || String(mapping.productId) !== String(this.dataset.productId) || !mapping.variants) {
            throw new Error('MAPPING_CONTRACT_INVALID');
          }
          this.mapping = mapping;
        } catch (error) {
          this.mappingError = error.message || 'MAPPING_LOAD_FAILED';
          console.error('KYMA bundle: native mapping unavailable.', error);
        }
      }

      async verifyNativeAvailability(intent = this.getIntent(), { fresh = false } = {}) {
        const key = intent.availabilityKey;
        if (!intent.storefrontPublished) {
          this.nativeAvailability = { key, status: 'DRAFT_PRODUCT', available: false };
          this.updateIntent({ skipAvailabilityCheck: true });
          return false;
        }
        if (!intent.cartLine?.id || !key || !this.dataset.availabilityEndpoint) {
          this.nativeAvailability = { key, status: 'AVAILABILITY_CONTRACT_MISSING', available: false };
          this.updateIntent({ skipAvailabilityCheck: true });
          return false;
        }
        if (!fresh && this.nativeAvailability.key === key && ['AVAILABLE', 'UNAVAILABLE', 'ERROR'].includes(this.nativeAvailability.status)) {
          return this.nativeAvailability.available;
        }
        if (!fresh && this.pendingAvailabilityKey === key) return false;

        this.availabilityAbortController?.abort();
        const controller = new AbortController();
        this.availabilityAbortController = controller;
        const version = ++this.availabilityVersion;
        let timedOut = false;
        const timeout = setTimeout(() => {
          timedOut = true;
          controller.abort();
        }, Number(this.dataset.availabilityTimeoutMs) || 8000);
        this.pendingAvailabilityKey = key;
        this.nativeAvailability = { key, status: 'CHECKING', available: false };
        this.updateIntent({ skipAvailabilityCheck: true });

        try {
          const response = await fetch(this.dataset.availabilityEndpoint, {
            method: 'POST',
            credentials: 'omit',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              query: 'query NativeVariantAvailability($id:ID!){node(id:$id){...on ProductVariant{id availableForSale requiresComponents product{id}}}}',
              variables: { id: `gid://shopify/ProductVariant/${intent.cartLine.id}` },
            }),
            signal: controller.signal,
          });
          if (!response.ok) throw new Error(`HTTP_${response.status}`);
          const payload = await response.json();
          if (Array.isArray(payload?.errors) && payload.errors.length) throw new Error('GRAPHQL_ERROR');
          const node = payload?.data?.node;
          const expectedProductId = intent.tier === 1 ? String(this.dataset.productId) : String(this.mapping?.parents?.[String(intent.tier)]?.id || '');
          const nodeProductId = String(node?.product?.id || '').split('/').at(-1);
          const contractValid =
            String(node?.id || '').split('/').at(-1) === String(intent.cartLine.id) &&
            nodeProductId === expectedProductId &&
            node?.availableForSale === true &&
            (intent.tier === 1 || node?.requiresComponents === true);
          if (version !== this.availabilityVersion || key !== this.getIntent().availabilityKey) return false;
          this.nativeAvailability = {
            key,
            status: contractValid ? 'AVAILABLE' : 'UNAVAILABLE',
            available: contractValid,
            productId: nodeProductId,
            requiresComponents: node?.requiresComponents === true,
          };
          return contractValid;
        } catch (error) {
          if ((error.name === 'AbortError' && !timedOut) || version !== this.availabilityVersion) return false;
          this.nativeAvailability = { key, status: 'ERROR', available: false, error: error.message || String(error) };
          return false;
        } finally {
          clearTimeout(timeout);
          if (version === this.availabilityVersion) {
            this.pendingAvailabilityKey = null;
            this.updateIntent({ skipAvailabilityCheck: true });
          }
        }
      }

      collectOptionValues() {
        const values = [];
        this.variants.forEach((variant) => {
          variant.options.forEach((value, index) => {
            values[index] ||= [];
            if (!values[index].includes(value)) values[index].push(value);
          });
        });
        return values;
      }

      initializeTier(tier) {
        const quantity = Number(tier.dataset.tier);
        const defaults = this.parseDefaults(tier.dataset.defaults, quantity);
        const slotsRoot = tier.querySelector('[data-tier-slots]');
        if (!slotsRoot) return;

        const slots = this.dataset.sameVariantPerTier === 'true' ? 1 : quantity;
        for (let index = 0; index < slots; index += 1) {
          slotsRoot.append(this.createSlot(tier, index, defaults[index]));
        }
        [...slotsRoot.querySelectorAll('[data-slot]')].forEach((slot) => this.refreshSlotAvailability(slot));
        this.updateTier(tier);
      }

      parseDefaults(value, quantity) {
        const fallback = this.variants[0]?.options || [];
        const defaults = String(value || '')
          .split(';')
          .filter(Boolean)
          .map((entry) => entry.split('|'));
        while (defaults.length < quantity) defaults.push([...fallback]);
        return defaults.slice(0, quantity);
      }

      createSlot(tier, index, defaults) {
        const row = document.createElement('div');
        row.className = 'kyma-bundle__slot';
        row.dataset.slot = String(index);

        const number = document.createElement('span');
        number.className = 'kyma-bundle__slot-number';
        number.textContent = `#${index + 1}`;
        row.append(number);

        this.optionValues.forEach((values, optionIndex) => {
          const select = document.createElement('select');
          select.className = 'kyma-bundle__select';
          select.dataset.optionIndex = String(optionIndex);
          select.setAttribute('aria-label', `${this.dataset.itemLabel || 'Poêle'} ${this.dataset.sameVariantPerTier === 'true' ? 'du lot' : index + 1}, ${this.optionNames[optionIndex] || `option ${optionIndex + 1}`}`);

          values.forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = this.optionLabel(value, optionIndex, tier);
            option.selected = defaults?.[optionIndex] === value;
            select.append(option);
          });
          row.append(select);
        });
        return row;
      }

      refreshSlotAvailability(slot) {
        if (!slot) return;
        const selects = [...slot.querySelectorAll('.kyma-bundle__select')];
        const current = selects.map((select) => select.value);
        selects.forEach((select, optionIndex) => {
          [...select.options].forEach((option) => {
            const available = this.variants.some(
              (variant) => variant.available &&
                variant.options[optionIndex] === option.value &&
                variant.options.every((value, index) => index >= optionIndex || value === current[index])
            );
            option.disabled = !available;
            option.hidden = !available && !option.selected;
          });
          const selectedUnavailable = Boolean(select.selectedOptions[0]?.disabled);
          select.toggleAttribute('aria-invalid', selectedUnavailable);
          select.disabled = ![...select.options].some((option) => !option.disabled);
        });
      }

      optionLabel(value, optionIndex, tier) {
        if (tier?.dataset.priceFromMapping === 'true') {
          const prices = this.mapping?.tierUnitPrices?.[tier.dataset.tier];
          const all = this.variants.map(v => prices?.[v.id]);
          const chosen = this.variants.filter(v => v.options[optionIndex] === value).map(v => prices?.[v.id]);
          if (!all.length || all.some(x => !Number.isSafeInteger(x)) || !chosen.length) return value;
          const supplement = Math.min(...chosen) - Math.min(...all);
          return supplement > 0 ? `${value} (+${this.formatMoney(supplement)})` : value;
        }
        const allPrices = this.variants.map((variant) => variant.price);
        const baseline = Math.min(...allPrices);
        const pricesForValue = this.variants
          .filter((variant) => variant.options[optionIndex] === value)
          .map((variant) => variant.price);
        const supplement = pricesForValue.length ? Math.min(...pricesForValue) - baseline : 0;
        return supplement > 0 ? `${value} (+${this.formatMoney(supplement)})` : value;
      }

      selectTier(number) {
        this.tiers.forEach((tier) => {
          const selected = tier.dataset.tier === String(number);
          tier.classList.toggle('is-selected', selected);
          tier.querySelector('[data-tier-choices]')?.toggleAttribute('hidden', !selected);
        });
        this.updateIntent();
      }

      updateTier(tier) {
        if (!tier) return;
        [...tier.querySelectorAll('[data-slot]')].forEach((slot) => this.refreshSlotAvailability(slot));
        const variants = this.selectedVariants(tier);
        const fixedPrice = tier.dataset.fixedPriceCents;
        const hasFixedPrice = fixedPrice !== undefined && fixedPrice !== '';
        const fixedPriceValid = !hasFixedPrice || (/^\d+$/.test(fixedPrice) && Number.isSafeInteger(Number(fixedPrice)) && Number(fixedPrice) > 0);
        let valid = variants.length === Number(tier.dataset.tier) && variants.every(Boolean) && fixedPriceValid;
        const compareCents = valid ? variants.reduce((sum, variant) => sum + variant.price, 0) : 0;
        const discountBasisPoints = Number(tier.dataset.discountBasisPoints || 0);
        let totalCents = hasFixedPrice ? Number(fixedPrice) : Math.round((compareCents * (10000 - discountBasisPoints)) / 10000);
        const exactParent = tier.dataset.priceFromMapping === 'true';
        if (exactParent) {
          const mapped = valid ? this.mapping?.variants?.[this.canonicalKey(Number(tier.dataset.tier), variants.map(v => v.id))] : null;
          valid = valid && Boolean(mapped?.id) && Number.isSafeInteger(mapped.price) && mapped.price > 0;
          totalCents = valid ? mapped.price : 0;
          [...tier.querySelectorAll('[data-slot] select')].forEach(select => {
            [...select.options].forEach(option => { option.textContent = this.optionLabel(option.value, Number(select.dataset.optionIndex), tier); });
          });
        }

        tier.dataset.valid = String(valid);
        tier.dataset.compareCents = String(compareCents);
        tier.dataset.totalCents = String(totalCents);
        const total = tier.querySelector('[data-tier-total]');
        const compare = tier.querySelector('[data-tier-compare]');
        if (total) {
          if (valid) total.textContent = this.formatMoney(totalCents);
          else if (!exactParent || this.mapping || this.mappingError) total.textContent = 'Indisponible';
          // Before the initial mapping response, preserve the Liquid-rendered known default total.
        }
        const unit = tier.querySelector('[data-tier-unit-price]');
        if (unit && valid) unit.textContent = this.formatMoney(totalCents / Number(tier.dataset.unitPriceDivisor || 1)) + ' / pack';
        if (unit) unit.hidden = !valid && Boolean(this.mapping || this.mappingError);
        if (compare) {
          compare.textContent = this.formatMoney(compareCents);
          compare.toggleAttribute('hidden', !valid || (hasFixedPrice ? totalCents === compareCents : discountBasisPoints === 0));
        }
      }

      selectedVariants(tier) {
        const selected = [...tier.querySelectorAll('[data-slot]')].map((slot) => {
          const options = [...slot.querySelectorAll('select')].map((select) => select.value);
          return this.variants.find(
            (variant) => variant.available && variant.options.length === options.length && variant.options.every((value, index) => value === options[index])
          );
        });
        return this.dataset.sameVariantPerTier === 'true' ? Array(Number(tier.dataset.tier)).fill(selected[0]) : selected;
      }

      updateIntent({ skipAvailabilityCheck = false } = {}) {
        const tier = this.tiers.find((candidate) => candidate.classList.contains('is-selected'));
        if (!tier || !this.intentForm) return;
        this.updateTier(tier);

        const variants = this.selectedVariants(tier);
        const quantity = Number(tier.dataset.tier);
        const componentIds = variants.filter(Boolean).map((variant) => variant.id);
        const canonicalKey = this.canonicalKey(quantity, componentIds);
        const mapped = quantity === 1 ? { id: componentIds[0], price: Number(tier.dataset.totalCents) } : this.mapping?.variants?.[canonicalKey];
        const displayTotalCents = Number(tier.dataset.totalCents);
        const mappingValid = Boolean(mapped?.id) && Number(mapped.price) === displayTotalCents;
        const cartLine = mappingValid ? { id: String(mapped.id), quantity: 1 } : null;
        const presentationGifts = tier.dataset.giftKeys !== undefined ? tier.dataset.giftKeys.split('|').filter(Boolean) : quantity >= 3 ? ['board', 'spatula'] : quantity >= 2 ? ['spatula'] : [];
        const storefrontPublished = this.dataset.storefrontPublished === 'true';
        const availabilityKey = cartLine ? `${quantity}:${cartLine.id}` : null;
        const baseValid = this.quantityContractValid && storefrontPublished && tier.dataset.valid === 'true' && variants.length === quantity && mappingValid;
        if (this.nativeAvailability.status === 'CHECKING' && this.nativeAvailability.key !== availabilityKey) {
          this.availabilityAbortController?.abort();
          this.availabilityVersion += 1;
          this.pendingAvailabilityKey = null;
          this.nativeAvailability = { key: availabilityKey, status: storefrontPublished ? 'UNCHECKED' : 'DRAFT_PRODUCT', available: false };
        }
        const availability = this.nativeAvailability.key === availabilityKey ? this.nativeAvailability : { key: availabilityKey, status: storefrontPublished ? 'UNCHECKED' : 'DRAFT_PRODUCT', available: false };

        this.intentForm.replaceChildren();
        if (cartLine) {
          this.intentForm.append(this.hiddenInput('id', cartLine.id), this.hiddenInput('quantity', '1'));
        }

        const mainVariantInput = this.mainForm?.querySelector('input[name="id"]');
        if (mainVariantInput && cartLine) mainVariantInput.value = cartLine.id;
        else if (mainVariantInput && tier.dataset.priceFromMapping === 'true') mainVariantInput.value = '';
        if (this.mainForm && cartLine) {
          let mainQuantityInput = this.mainForm.querySelector('[data-kyma-bundle-quantity]');
          if (!mainQuantityInput) {
            mainQuantityInput = this.hiddenInput('quantity', '1');
            mainQuantityInput.dataset.kymaBundleQuantity = '';
            this.mainForm.append(mainQuantityInput);
          }
          mainQuantityInput.value = '1';
          [...this.mainForm.elements]
            .filter((control) => control.name === 'quantity' && control !== mainQuantityInput)
            .forEach((control) => {
              control.disabled = true;
              control.dataset.kymaBundleDisabledQuantity = '';
            });
        }

        this.intent = {
          tier: quantity,
          tierRow: Number(tier.dataset.tierRow),
          componentVariantIds: componentIds,
          canonicalKey,
          cartLine,
          presentationGifts,
          digitalGuideUrl: this.dataset.digitalGuideUrl || null,
          digitalGuideLabel: this.dataset.digitalGuideLabel || null,
          compareCents: Number(tier.dataset.compareCents),
          displayTotalCents,
          discountBasisPoints: Number(tier.dataset.discountBasisPoints || 0),
          mappingStatus: !storefrontPublished ? 'DRAFT_PRODUCT' : tier.dataset.valid !== 'true' ? 'VARIANT_UNAVAILABLE' : quantity === 1 ? 'DIRECT_VARIANT' : mapped ? (mappingValid ? 'MAPPED' : 'PRICE_MISMATCH') : this.mappingError || 'MAPPING_PENDING_OR_MISSING',
          serverVerified: this.dataset.serverVerified === 'true',
          serverAvailability: 'SERVER_AUTHORITATIVE_NOT_IN_MAP',
          storefrontPublished,
          availabilityKey,
          availabilityStatus: availability.status,
          nativeVariantAvailable: availability.available,
          baseValid,
          valid: baseValid && availability.available,
        };

        this.updateAddButtons(this.intent.valid);

        this.dispatchEvent(new CustomEvent('kyma:bundle-change', { bubbles: true, detail: this.getIntent() }));
        if (!skipAvailabilityCheck && baseValid && (!availability.key || availability.status === 'UNCHECKED')) {
          queueMicrotask(() => this.verifyNativeAvailability(this.getIntent()));
        }
      }

      hiddenInput(name, value) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        return input;
      }

      updateAddButtons(valid) {
        if (!this.mainForm) return;
        [...this.mainForm.querySelectorAll('button[name="add"], .product-form__submit')].forEach((button) => {
          if (!valid && !button.disabled) {
            button.disabled = true;
            button.dataset.kymaBundleDisabled = '';
          } else if (valid && button.hasAttribute('data-kyma-bundle-disabled')) {
            button.disabled = false;
            delete button.dataset.kymaBundleDisabled;
          }
        });
      }

      canonicalKey(quantity, ids) {
        const sorted = [...ids].sort((left, right) => {
          const a = BigInt(left);
          const b = BigInt(right);
          return a < b ? -1 : a > b ? 1 : 0;
        });
        return `${quantity}:${sorted.join('+')}`;
      }

      getIntent() {
        return structuredClone(this.intent || {});
      }

      formatMoney(cents) {
        return new Intl.NumberFormat(this.dataset.moneyLocale || 'fr-FR', {
          style: 'currency',
          currency: this.dataset.currency || 'EUR',
        }).format(cents / 100);
      }
    }
  );
}
